import { codeUnitString } from "../code-unit-string.js";
import { cpCookieSessionResponseSchema, cpTokenPairResponseSchema } from "../session-transport.js";
import { REFUSED_DISPLAY_CODE_POINT, base64Url32BytesSchema, base64UrlTextSchema, displayTextSchema, lowerHostSchema } from "./kernel-wire-internal.js";
import { stepUpAllowCredentialSchema, stepUpPublicKeyOptionsSchema, stepUpTransportsOf } from "./step-up.js";
import { z } from "zod";
//#region src/kernel/second-factor.ts
/**
* The second-factor wire of both planes (kernel apps, WS-1b, decision 5, R-003, R-042).
*
* A login whose first factor passed and whose session needs a passkey answers a wait, not a
* session. The wait carries a purpose-bound second-factor token. The client then posts the token to
* `POST /v1/auth/second-factor/options`, runs the WebAuthn ceremony that the answer names, and posts
* the result to `POST /v1/auth/second-factor/verify`. cp-auth-api (WS-6a) and core-api (WS-10a)
* serve the two routes. The CP console (WS-9b), the kernel shell (WS-15) and the system surfaces
* (WS-25) call them. Every one of them imports the names of this module (R-042).
*
* The words: a `mode` says why the server waits (four DP modes; the CP uses `assert` and `enrol`).
* A `ceremony` says which WebAuthn call the browser runs: `authenticate` (`startAuthentication`)
* or `register` (`startRegistration`).
*
* Browser-safe: no Node builtin and no Node global. Every schema is `.strict()` at every level
* except `clientExtensionResults`, the one open member of a WebAuthn response. No schema uses
* `.transform()`.
*/
/** How long a second-factor token lives: 300 s. One use. */
const SECOND_FACTOR_TOKEN_TTL_SECONDS = 300;
/** The longest second-factor token, in characters. The DP token is a signed JWT; the CP token is 43 base64url characters. */
const SECOND_FACTOR_TOKEN_MAX_LENGTH = 4096;
/** A second-factor token: 1 to 4,096 characters of `[A-Za-z0-9._-]`. The server alone reads its content. */
const secondFactorTokenSchema = z.string().min(1).max(SECOND_FACTOR_TOKEN_MAX_LENGTH).regex(/^[A-Za-z0-9._-]+$/, "a second-factor token holds only [A-Za-z0-9._-]");
/**
* Why the server waits for a second factor:
*
* - `assert`: the user has a passkey of this relying party; prove it;
* - `legacy_assert`: the user has only a passkey of the old relying party `<dp>`; prove it, and the
*   server answers a new `enrol` wait (DP only);
* - `enrol`: the user has no usable passkey; register one;
* - `recovery_enrol`: a recovery proof passed, and the user registers a new passkey. On the DP the
*   verify stores the passkey and answers no session (I-8). On the CP (R-152) the verify answers a
*   session with the factors `[first, 'rec', 'pop']`, and it ends every older session of the operator.
*/
const SECOND_FACTOR_MODES = [
	"assert",
	"legacy_assert",
	"enrol",
	"recovery_enrol"
];
/** The two WebAuthn ceremonies: `authenticate` runs `startAuthentication`, `register` runs `startRegistration`. */
const SECOND_FACTOR_CEREMONIES = ["authenticate", "register"];
/** The ceremony of a mode: `assert` and `legacy_assert` give `authenticate`; `enrol` and `recovery_enrol` give `register`. Pure. */
function secondFactorCeremonyOf(mode) {
	return mode === "assert" || mode === "legacy_assert" ? "authenticate" : "register";
}
/**
* The four ways back to a CP session for an operator whose passkey is lost or unusable (kernel apps
* stage 5, WS-31a1, R-150, R-152). Each one replaces the passkey step and never the first factor:
*
* - `code`: a recovery code that the CP issued at the first passkey and showed once;
* - `email`: a one-time code that the CP mails to the operator's own address (WS-31a3);
* - `break-glass`: a one-time token that a person with Secret Manager and cluster access armed
*   for a platform administrator (WS-31a2);
* - `admin-reset`: a second platform administrator resets the passkeys through the
*   `operator-passkey-reset` step-up (R-046). The operator then registers a new passkey.
*/
const RECOVERY_METHODS = [
	"code",
	"email",
	"break-glass",
	"admin-reset"
];
/**
* The recovery methods that a wait offers: 0 to 4 known methods, each at most once, in the order of
* {@link RECOVERY_METHODS}. It refuses an unknown method, a repeat and a list out of order.
*/
const recoveryMethodListSchema = z.array(z.enum(RECOVERY_METHODS)).max(RECOVERY_METHODS.length).superRefine((methods, ctx) => {
	const ranks = methods.map((method) => RECOVERY_METHODS.indexOf(method));
	if (ranks.some((rank, i) => i > 0 && rank <= ranks[i - 1])) ctx.addIssue({
		code: "custom",
		message: "the recovery methods repeat or break the order of RECOVERY_METHODS"
	});
});
/**
* The strict parse of {@link SecondFactorWait}. It refuses `recoveryMethods` beside a mode other
* than `assert`.
*/
const secondFactorWaitSchema = z.object({
	status: z.literal("second_factor_required"),
	mode: z.enum(SECOND_FACTOR_MODES),
	secondFactorToken: secondFactorTokenSchema,
	expiresIn: z.int().min(1).max(300),
	recoveryMethods: recoveryMethodListSchema.optional()
}).strict().refine((wait) => wait.recoveryMethods === void 0 || wait.mode === "assert", {
	path: ["recoveryMethods"],
	message: "only an assert wait offers recovery methods"
});
/** The strict parse of {@link PasskeyRegistered}. */
const passkeyRegisteredSchema = z.object({ status: z.literal("passkey_registered") }).strict();
/** The strict parse of {@link SecondFactorOptionsRequest} (R-042). */
const secondFactorOptionsRequestSchema = z.object({ secondFactorToken: secondFactorTokenSchema }).strict();
/** The longest `rp.name`, `user.name` and `user.displayName` of the creation options, in UTF-16 code units. */
const SECOND_FACTOR_NAME_MAX = 256;
const DISPLAY_TEXT_ELLIPSIS = "...";
/**
* A name that the creation options and the verify body admit, from a name that a plane stored.
*
* Who calls it: the options producers of cp-auth-api (WS-6a) and core-api (WS-10a), on the relying
* party name, the user name (the email address) and the user display name, before they build the
* creation options. A client (WS-9b, WS-15, WS-25) calls it on a device name that a person typed.
* core-api stores a display name of 1 to 256 code units with no code point rule, so a name such as
* "Ali" U+200C "Reza" or an emoji sequence with U+200D fails {@link secondFactorRegistrationOptionsSchema}
* when a producer copies it unchanged.
*
* The rule:
*
* 1. It drops each refused code point: a control (`Cc`), a format character (`Cf`, the zero-width
*    joiners and the bidi controls among them), a lone surrogate (`Cs`), and a line or paragraph
*    separator (`Zl`, `Zp`). A passkey manager shows the name to the person, so the result holds no
*    invisible text and no text that turns the line around.
* 2. A result longer than `max` code units keeps its head up to a code point boundary and ends in
*    `...`, at most `max` code units in all.
*
* The result is empty only when `text` holds no admitted code point. The schema refuses an empty
* `rp.name`, `user.name` or `deviceName`, and admits an empty `user.displayName`. It throws
* `TypeError` for a `max` that is not an integer from 4 to {@link SECOND_FACTOR_NAME_MAX}. Pure.
*/
function secondFactorDisplayText(text, max) {
	if (!Number.isInteger(max) || max < 4 || max > 256) throw new TypeError(`secondFactorDisplayText: max must be an integer from 4 to 256`);
	let kept = "";
	for (const ch of text) if (!REFUSED_DISPLAY_CODE_POINT.test(ch)) kept += ch;
	if (kept.length <= max) return kept;
	let end = max - 3;
	const last = kept.charCodeAt(end - 1);
	if (last >= 55296 && last <= 56319) end -= 1;
	return kept.slice(0, end) + DISPLAY_TEXT_ELLIPSIS;
}
/**
* The strict parse of {@link SecondFactorRegistrationOptions}. Bounds: a 43-character challenge; an
* `rp.name` of 1 to 256 and an `rp.id` that is a lower-case host; a `user.id` of 1 to 86 base64url
* characters (64 bytes); a `user.name` of 1 to 256 and a `user.displayName` of 0 to 256 with no
* refused code point (build each name with {@link secondFactorDisplayText}); 1 to 16 key parameters; a timeout of 1 to 600,000 ms; 0 to 32 excluded
* credentials; 0 to 3 unique hints.
*/
const secondFactorRegistrationOptionsSchema = z.object({
	challenge: base64Url32BytesSchema,
	rp: z.object({
		name: displayTextSchema(1, 256),
		id: lowerHostSchema
	}).strict(),
	user: z.object({
		id: base64UrlTextSchema(86),
		name: displayTextSchema(1, 256),
		displayName: displayTextSchema(0, 256)
	}).strict(),
	pubKeyCredParams: z.array(z.object({
		alg: z.int().min(-65535).max(65535),
		type: z.literal("public-key")
	}).strict()).min(1).max(16),
	timeout: z.int().min(1).max(6e5),
	attestation: z.literal("none"),
	excludeCredentials: z.array(stepUpAllowCredentialSchema).max(32),
	authenticatorSelection: z.object({
		residentKey: z.enum([
			"discouraged",
			"preferred",
			"required"
		]),
		userVerification: z.literal("required"),
		requireResidentKey: z.boolean().optional(),
		authenticatorAttachment: z.enum(["platform", "cross-platform"]).optional()
	}).strict(),
	extensions: z.object({ credProps: z.boolean().optional() }).strict().optional(),
	hints: z.array(z.enum([
		"hybrid",
		"security-key",
		"client-device"
	])).max(3).refine((hints) => new Set(hints).size === hints.length, "a hint occurs twice").optional()
}).strict();
/** The strict parse of {@link SecondFactorOptionsResponse}, discriminated on `ceremony`. */
const secondFactorOptionsResponseSchema = z.discriminatedUnion("ceremony", [z.object({
	ceremony: z.literal("authenticate"),
	publicKeyOptions: stepUpPublicKeyOptionsSchema
}).strict(), z.object({
	ceremony: z.literal("register"),
	publicKeyOptions: secondFactorRegistrationOptionsSchema
}).strict()]);
/**
* The registration that the register verify gives to `verifyRegistrationResponse`, from the
* registration of a parsed verify body (R-096).
*
* Who calls it: the register verify of cp-auth-api (WS-6a) and core-api (WS-10a), after
* {@link secondFactorVerifyRequestSchema} admits the body and before `verifyRegistrationResponse`.
* The library copies `response.transports` into `registrationInfo.credential.transports`, and the
* producer stores that list. So the stored list holds the known words only.
*
* The result equals `registration` in every member except `response.transports`:
*
* - a `transports` list becomes {@link stepUpTransportsOf} of it: the known words, each once, in
*   their first order. A list of unknown words alone becomes an empty list, and the registration
*   stays valid;
* - an absent `transports` stays absent.
*
* It does not check the other members; the schema parse did that. It never throws and does not
* change `registration`. Pure.
*/
function webAuthnRegistrationForVerify(registration) {
	const { transports, ...rest } = registration.response;
	if (transports === void 0) return {
		...registration,
		response: rest
	};
	return {
		...registration,
		response: {
			...rest,
			transports: stepUpTransportsOf(transports)
		}
	};
}
const clientExtensionResultsSchema = z.record(z.string().max(64), z.unknown()).refine((results) => Object.keys(results).length <= 16, "at most 16 client extension results");
const attachmentSchema = z.enum(["platform", "cross-platform"]).optional();
function sameIdAndRawId(credential) {
	return credential.id === credential.rawId;
}
/**
* The strict parse of {@link WebAuthnAuthenticationResponse}. Caps in characters: `id` and `rawId`
* 1 to 1,024 and equal; `clientDataJSON` and `authenticatorData` 1 to 4,096; `signature` and
* `userHandle` 1 to 1,024. Each is base64url with no padding.
*/
const webAuthnAuthenticationResponseSchema = z.object({
	id: base64UrlTextSchema(1024),
	rawId: base64UrlTextSchema(1024),
	type: z.literal("public-key"),
	response: z.object({
		clientDataJSON: base64UrlTextSchema(4096),
		authenticatorData: base64UrlTextSchema(4096),
		signature: base64UrlTextSchema(1024),
		userHandle: base64UrlTextSchema(1024).optional()
	}).strict(),
	authenticatorAttachment: attachmentSchema,
	clientExtensionResults: clientExtensionResultsSchema
}).strict().refine(sameIdAndRawId, {
	path: ["rawId"],
	message: "rawId must equal id"
});
/**
* The strict parse of {@link WebAuthnRegistrationResponse}. Caps in characters: `id` and `rawId`
* 1 to 1,024 and equal; `clientDataJSON` and `authenticatorData` 1 to 4,096; `attestationObject` 1
* to 16,384; `publicKey` 1 to 1,024; at most 16 transports of at most 64 code units each.
*
* A transport word is free text within its cap. WebAuthn requires a relying party to ignore an
* unknown word, so the parse admits it and keeps the registration (R-096). The parse does not
* drop the word, because a zod `.transform()` cannot render to JSON Schema.
* {@link webAuthnRegistrationForVerify} drops it before the library and the store see it.
*/
const webAuthnRegistrationResponseSchema = z.object({
	id: base64UrlTextSchema(1024),
	rawId: base64UrlTextSchema(1024),
	type: z.literal("public-key"),
	response: z.object({
		clientDataJSON: base64UrlTextSchema(4096),
		attestationObject: base64UrlTextSchema(16384),
		authenticatorData: base64UrlTextSchema(4096).optional(),
		transports: z.array(codeUnitString().max(64)).max(16).optional(),
		publicKeyAlgorithm: z.int().min(-65535).max(65535).optional(),
		publicKey: base64UrlTextSchema(1024).optional()
	}).strict(),
	authenticatorAttachment: attachmentSchema,
	clientExtensionResults: clientExtensionResultsSchema
}).strict().refine(sameIdAndRawId, {
	path: ["rawId"],
	message: "rawId must equal id"
});
/** The longest device name a register verify carries, in UTF-16 code units. */
const SECOND_FACTOR_DEVICE_NAME_MAX = 256;
/** The strict parse of {@link SecondFactorVerifyRequest} (R-042). */
const secondFactorVerifyRequestSchema = z.discriminatedUnion("ceremony", [z.object({
	ceremony: z.literal("authenticate"),
	secondFactorToken: secondFactorTokenSchema,
	response: webAuthnAuthenticationResponseSchema
}).strict(), z.object({
	ceremony: z.literal("register"),
	secondFactorToken: secondFactorTokenSchema,
	response: webAuthnRegistrationResponseSchema,
	deviceName: displayTextSchema(1, 256).optional()
}).strict()]);
/**
* The routes of the CP recovery proofs. Each is public: the second-factor token in the body is the
* credential, as on the options and verify routes. A passed proof answers a {@link SecondFactorWait}
* with mode `recovery_enrol` and a new token, and the old token is spent. The client then runs the
* options and verify routes, which register a new passkey and answer a session. No proof route
* answers a session (R-152).
*
* - `code`: `POST`, body {@link RecoveryCodeProofRequest} (WS-31a1);
* - `emailSend`: `POST`, body {@link RecoveryEmailSendRequest}, answer 204 (WS-31a3);
* - `emailVerify`: `POST`, body {@link RecoveryEmailVerifyRequest} (WS-31a3);
* - `breakGlass`: `POST`, body {@link RecoveryBreakGlassRequest} (WS-31a2).
*/
const SECOND_FACTOR_RECOVERY_ROUTES = {
	code: "/v1/auth/second-factor/recovery/code",
	emailSend: "/v1/auth/second-factor/recovery/email/send",
	emailVerify: "/v1/auth/second-factor/recovery/email/verify",
	breakGlass: "/v1/auth/second-factor/recovery/break-glass"
};
/**
* The route of the signed-in operator's own recovery codes: `GET` answers
* {@link RecoveryCodesStatus}, and `POST` with no body replaces every code and answers
* {@link RecoveryCodesIssued} once. Both need a first-party session; `POST` also needs a session
* that passed the passkey.
*/
const RECOVERY_CODES_ROUTE = "/v1/auth/recovery-codes";
/**
* The two 400 messages that the recovery proof routes add to the second-factor refusals. Each is a
* fixed word:
*
* - `invalid`: the proof did not match. One word for every cause: an unknown, used or mistyped code.
*   The wait stays, until its fifth failed proof deletes it;
* - `unavailable`: this wait takes no recovery proof now: it is not an `assert` wait, it is a
*   recovery wait already, the operator reached the failure limit of the hour, or the method is
*   not available on this CP.
*/
const RECOVERY_REFUSALS = {
	invalid: "recovery_invalid",
	unavailable: "recovery_unavailable"
};
/** The number of recovery codes in one issue: 10. */
const RECOVERY_CODE_COUNT = 10;
/** The 32 characters of a recovery code: the upper-case letters and digits less `I`, `O`, `0` and `1`. */
const RECOVERY_CODE_ALPHABET = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
/** A recovery code as the CP shows it: three groups of four alphabet characters, joined by `-`. */
const RECOVERY_CODE_PATTERN = /^[A-HJ-NP-Z2-9]{4}-[A-HJ-NP-Z2-9]{4}-[A-HJ-NP-Z2-9]{4}$/;
/**
* A recovery code as a person types it: three groups of four letters or digits in either case, with
* an optional `-` between two groups. {@link recoveryCodeKey} gives the key that the server
* compares.
*/
const RECOVERY_CODE_INPUT_PATTERN = /^[A-Za-z0-9]{4}-?[A-Za-z0-9]{4}-?[A-Za-z0-9]{4}$/;
/** The strict parse of one shown recovery code: 14 characters of {@link RECOVERY_CODE_PATTERN}. */
const recoveryCodeSchema = z.string().length(14).regex(RECOVERY_CODE_PATTERN, "a recovery code XXXX-XXXX-XXXX");
/** The codes of one issue: exactly {@link RECOVERY_CODE_COUNT} codes, no repeat. */
const recoveryCodeListSchema = z.array(recoveryCodeSchema).length(10).superRefine((codes, ctx) => {
	if (new Set(codes).size !== codes.length) ctx.addIssue({
		code: "custom",
		message: "a recovery code occurs twice"
	});
});
/**
* The 12-character key of a typed recovery code, or `null`.
*
* Who calls it: cp-auth-api on the `code` of a proof, before the prefix lookup and the hash compare;
* a client may call it to check a code before it posts. It drops the `-` separators and upper-cases
* the letters. It answers `null` for a text outside {@link RECOVERY_CODE_INPUT_PATTERN} and for a key
* with a character outside {@link RECOVERY_CODE_ALPHABET} (`I`, `O`, `0` or `1`). No code has such a
* key, so the server refuses it as `recovery_invalid`. Never throws. Pure.
*/
function recoveryCodeKey(input) {
	if (!RECOVERY_CODE_INPUT_PATTERN.test(input)) return null;
	const key = input.replaceAll("-", "").toUpperCase();
	for (const ch of key) if (!"ABCDEFGHJKLMNPQRSTUVWXYZ23456789".includes(ch)) return null;
	return key;
}
/** The strict parse of {@link RecoveryCodeProofRequest}. */
const recoveryCodeProofRequestSchema = z.object({
	secondFactorToken: secondFactorTokenSchema,
	code: z.string().min(12).max(14).regex(RECOVERY_CODE_INPUT_PATTERN, "a recovery code XXXX-XXXX-XXXX")
}).strict();
/** The emailed one-time code: six decimal digits. */
const RECOVERY_EMAIL_CODE_PATTERN = /^[0-9]{6}$/;
/** The strict parse of {@link RecoveryEmailSendRequest}. */
const recoveryEmailSendRequestSchema = z.object({ secondFactorToken: secondFactorTokenSchema }).strict();
/** The strict parse of {@link RecoveryEmailVerifyRequest}. */
const recoveryEmailVerifyRequestSchema = z.object({
	secondFactorToken: secondFactorTokenSchema,
	code: z.string().length(6).regex(RECOVERY_EMAIL_CODE_PATTERN, "six digits")
}).strict();
/** A break-glass token: 32 random bytes as 43 unpadded base64url characters. */
const BREAK_GLASS_TOKEN_PATTERN = /^[A-Za-z0-9_-]{43}$/;
/** The strict parse of {@link RecoveryBreakGlassRequest}. */
const recoveryBreakGlassRequestSchema = z.object({
	secondFactorToken: secondFactorTokenSchema,
	breakGlassToken: z.string().length(43).regex(BREAK_GLASS_TOKEN_PATTERN, "43 base64url characters")
}).strict();
/** The strict parse of {@link RecoveryCodesStatus}. */
const recoveryCodesStatusSchema = z.object({ remaining: z.int().min(0).max(10) }).strict();
/** The strict parse of {@link RecoveryCodesIssued}. */
const recoveryCodesIssuedSchema = z.object({ recoveryCodes: recoveryCodeListSchema }).strict();
/** The longest `redirectUrl` of a CP verify answer, in characters. */
const CP_SECOND_FACTOR_REDIRECT_MAX = 4096;
/** The strict parse of {@link CpSecondFactorVerifyAnswer}. A body with the keys of two arms fails all three. */
const cpSecondFactorVerifyAnswerSchema = z.union([
	cpCookieSessionResponseSchema.extend({ recoveryCodes: recoveryCodeListSchema.optional() }).strict(),
	cpTokenPairResponseSchema.extend({ recoveryCodes: recoveryCodeListSchema.optional() }).strict(),
	z.object({
		redirectUrl: z.string().min(1).max(CP_SECOND_FACTOR_REDIRECT_MAX),
		recoveryCodes: recoveryCodeListSchema.optional()
	}).strict()
]);
//#endregion
export { BREAK_GLASS_TOKEN_PATTERN, CP_SECOND_FACTOR_REDIRECT_MAX, RECOVERY_CODES_ROUTE, RECOVERY_CODE_ALPHABET, RECOVERY_CODE_COUNT, RECOVERY_CODE_INPUT_PATTERN, RECOVERY_CODE_PATTERN, RECOVERY_EMAIL_CODE_PATTERN, RECOVERY_METHODS, RECOVERY_REFUSALS, SECOND_FACTOR_CEREMONIES, SECOND_FACTOR_DEVICE_NAME_MAX, SECOND_FACTOR_MODES, SECOND_FACTOR_NAME_MAX, SECOND_FACTOR_RECOVERY_ROUTES, SECOND_FACTOR_TOKEN_MAX_LENGTH, SECOND_FACTOR_TOKEN_TTL_SECONDS, cpSecondFactorVerifyAnswerSchema, passkeyRegisteredSchema, recoveryBreakGlassRequestSchema, recoveryCodeKey, recoveryCodeListSchema, recoveryCodeProofRequestSchema, recoveryCodeSchema, recoveryCodesIssuedSchema, recoveryCodesStatusSchema, recoveryEmailSendRequestSchema, recoveryEmailVerifyRequestSchema, recoveryMethodListSchema, secondFactorCeremonyOf, secondFactorDisplayText, secondFactorOptionsRequestSchema, secondFactorOptionsResponseSchema, secondFactorRegistrationOptionsSchema, secondFactorTokenSchema, secondFactorVerifyRequestSchema, secondFactorWaitSchema, webAuthnAuthenticationResponseSchema, webAuthnRegistrationForVerify, webAuthnRegistrationResponseSchema };

//# sourceMappingURL=second-factor.js.map